import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Bot, Send, User, Sparkles, Search, ArrowRight, 
  MessageSquare, Loader2, Trash2, History, Crown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useSubscription } from '@/context/SubscriptionContext';
import axios from 'axios';
import { toast } from 'sonner';
import SEO from '@/components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AIResearchPage = () => {
  const { token, user } = useAuth();
  const { isPremium, isTrialActive, hasFullAccess } = useSubscription();
  
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    
    if (!token) {
      toast.error('Please log in to use the AI Research Assistant');
      return;
    }

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/ai/chat`, {
        message: userMessage,
        session_id: sessionId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response 
      }]);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to get response';
      if (error.response?.status === 429) {
        setMessages(prev => [...prev, { 
          role: 'system', 
          content: errorMsg 
        }]);
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const clearChat = async () => {
    if (sessionId && token) {
      try {
        await axios.delete(`${API}/ai/history/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) {}
    }
    setMessages([]);
    setSessionId(null);
    toast.success('Chat cleared');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const suggestedQuestions = [
    "Who are the most controversial MPs this week?",
    "Tell me about Labour councillors in Manchester",
    "What scandals have affected Conservative MPs recently?",
    "Which politicians have been in the news most?",
    "Find me councillors in Birmingham"
  ];

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4">
        <div className="text-center text-white max-w-md">
          <Bot className="h-16 w-16 mx-auto mb-6 text-cyan-400" />
          <h1 className="text-3xl font-black mb-4">AI Research Assistant</h1>
          <p className="text-slate-300 mb-8">
            Ask questions about any UK politician. Research MPs, councillors, scandals, and more.
          </p>
          <Link to="/register">
            <Button className="bg-cyan-500 hover:bg-cyan-600 text-white">
              Sign Up to Access
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20" data-testid="ai-research-page">
      <SEO 
        title="AI Political Research Assistant - Investigate UK Politicians"
        description="Ask questions about UK politicians and get instant AI-powered research. Investigate MPs, councillors, voting records, and political scandals with our research tool."
        keywords="political research UK, investigate politicians, MP research, councillor background, political AI, UK politician database, voting record lookup"
      />
      
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl">
              <Bot className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold">AI Research Assistant</h1>
              <p className="text-sm text-slate-500">Powered by GPT-4o</p>
            </div>
          </div>
          
          {messages.length > 0 && (
            <Button variant="outline" size="sm" onClick={clearChat}>
              <Trash2 className="h-4 w-4 mr-2" />
              Clear Chat
            </Button>
          )}
        </div>

        {/* Usage Notice for Free Users */}
        {!hasFullAccess && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3">
            <Crown className="h-5 w-5 text-amber-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-amber-800">
                Free users: 5 AI messages per day. 
                <Link to="/subscribe" className="font-bold ml-1 hover:underline">
                  Upgrade for unlimited
                </Link>
              </p>
            </div>
          </div>
        )}

        {/* Chat Area */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {/* Messages */}
          <div className="h-[500px] overflow-y-auto p-4 space-y-4">
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <div className="p-4 bg-slate-100 rounded-full mb-4">
                  <MessageSquare className="h-8 w-8 text-slate-400" />
                </div>
                <h2 className="text-lg font-semibold text-slate-700 mb-2">
                  Ask me anything about UK politics
                </h2>
                <p className="text-slate-500 mb-6 max-w-md">
                  I can help you research MPs, councillors, political scandals, 
                  and find information across 649 MPs and 19,000+ councillors.
                </p>
                
                <div className="w-full max-w-md">
                  <p className="text-xs font-medium text-slate-400 uppercase mb-3">
                    Try asking:
                  </p>
                  <div className="space-y-2">
                    {suggestedQuestions.map((q, idx) => (
                      <button
                        key={idx}
                        onClick={() => setInput(q)}
                        className="w-full text-left p-3 bg-slate-50 hover:bg-slate-100 rounded-lg text-sm text-slate-700 transition-colors"
                      >
                        <Search className="h-4 w-4 inline mr-2 text-slate-400" />
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <>
                {messages.map((msg, idx) => (
                  <div 
                    key={idx}
                    className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}
                  >
                    {msg.role !== 'user' && (
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        msg.role === 'system' ? 'bg-amber-100' : 'bg-cyan-100'
                      }`}>
                        {msg.role === 'system' ? (
                          <Crown className="h-4 w-4 text-amber-600" />
                        ) : (
                          <Bot className="h-4 w-4 text-cyan-600" />
                        )}
                      </div>
                    )}
                    
                    <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      msg.role === 'user' 
                        ? 'bg-blue-600 text-white' 
                        : msg.role === 'system'
                        ? 'bg-amber-50 text-amber-800 border border-amber-200'
                        : 'bg-slate-100 text-slate-800'
                    }`}>
                      <div className="whitespace-pre-wrap text-sm">
                        {msg.content}
                      </div>
                    </div>
                    
                    {msg.role === 'user' && (
                      <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                        <User className="h-4 w-4 text-white" />
                      </div>
                    )}
                  </div>
                ))}
                
                {loading && (
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center">
                      <Loader2 className="h-4 w-4 text-cyan-600 animate-spin" />
                    </div>
                    <div className="bg-slate-100 rounded-2xl px-4 py-3">
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Sparkles className="h-4 w-4" />
                        Thinking...
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Input Area */}
          <div className="border-t border-slate-200 p-4 bg-slate-50">
            <div className="flex gap-3">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about any UK politician..."
                className="flex-1 p-3 border border-slate-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                rows={2}
                disabled={loading}
                data-testid="ai-chat-input"
              />
              <Button 
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                className="bg-cyan-500 hover:bg-cyan-600 text-white px-6"
                data-testid="ai-send-button"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Send className="h-5 w-5" />
                )}
              </Button>
            </div>
            
            <p className="text-xs text-slate-400 mt-2 text-center">
              AI can make mistakes. Verify important information.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIResearchPage;
